import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// USER: a PBX extension owner (docs/PBX.md); signs in to their own phone page only
export type Role = 'PLATFORM' | 'ADMIN' | 'SUPERVISOR' | 'AGENT' | 'USER';
// admin_portal: may open the admin portal (admins, and agents or phone users in a permission group, 2026-10-15)
export interface User { id: number; username: string; name: string; role: Role; agent_id: number | null; tenant_id: number; admin_portal?: boolean }
// the tenant the session works in (a platform admin may switch away from their own, homeTenantId)
// modules: what the tenant runs (migration 026): CALLCENTER, PBX or both
export interface Tenant { id: number; name: string; slug: string; sip_domain?: string; status?: string; modules?: string[] }
export type Module = 'CALLCENTER' | 'PBX';

interface AuthState {
  token: string | null;
  user: User | null;
  tenant: Tenant | null;
  homeTenantId: number | null;
  // rights from /auth/me ("queues.edit", ...; ["*"] = everything), null until loaded (PHASE23)
  permissions: string[] | null;
  // the signed-in user's own PBX extension (from /auth/me), null when none
  extension: { id: number; number: string } | null;
  setSession: (token: string, user: User) => void;
  setTenant: (tenant: Tenant | null, homeTenantId: number | null) => void;
  setPermissions: (p: string[] | null) => void;
  setExtension: (e: { id: number; number: string } | null) => void;
  logout: () => void;
}

export const useAuth = create<AuthState>()(persist((set) => ({
  token: null,
  user: null,
  tenant: null,
  homeTenantId: null,
  permissions: null,
  extension: null,
  setSession: (token, user) => set({ token, user, permissions: null, extension: null }),
  setTenant: (tenant, homeTenantId) => set({ tenant, homeTenantId }),
  setPermissions: (permissions) => set({ permissions }),
  setExtension: (extension) => set({ extension }),
  logout: () => set({ token: null, user: null, tenant: null, homeTenantId: null, permissions: null, extension: null }),
}), { name: 'cc-auth' }));

// same rule as the API (backend/src/auth.ts hasRole): ADMIN passes SUPERVISOR checks, PLATFORM passes both
const RANK: Record<Role, number> = { USER: 0, AGENT: 1, SUPERVISOR: 2, ADMIN: 3, PLATFORM: 4 };
export function hasRole(role: Role | undefined | null, roles: readonly Role[]): boolean {
  return !!role && roles.some((r) => r === role || ((r === 'SUPERVISOR' || r === 'ADMIN') && RANK[role] > RANK[r]));
}

// the admin portal: admins and the old supervisors by role, agents and phone users by their permission groups
export function adminPortal(u: User | null | undefined): boolean {
  return hasRole(u?.role, ['SUPERVISOR']) || !!u?.admin_portal;
}

export function isSupervisor(u: User | null): boolean {
  return hasRole(u?.role, ['SUPERVISOR']);
}

// the module of each section that belongs to one (backend/src/perms.ts CATALOG)
const SECTION_MODULE: Record<string, Module> = Object.fromEntries([
  ...['campaigns', 'contacts', 'callbacks', 'dnc', 'broadcasts', 'dispositions', 'queues', 'agents', 'sla', 'crm', 'crm_agent'].map((s) => [s, 'CALLCENTER']),
  ...['extensions', 'pbx_groups', 'conferences', 'voicemail_boxes', 'pbx_settings'].map((s) => [s, 'PBX']),
]);
// does the tenant run this module? Until /auth/me has answered, the call center only (what every tenant had before)
export function hasModule(tenant: Tenant | null | undefined, m: Module): boolean {
  return (tenant?.modules ?? ['CALLCENTER']).includes(m);
}
export function useModule(m: Module): boolean {
  return useAuth((s) => hasModule(s.tenant, m));
}

// May the user do one of these ("section.action")? The API decides (backend/src/perms.ts); this only hides
// what would be refused. Until /auth/me has answered, ADMIN and PLATFORM may do everything, others nothing.
// A right of a module the tenant does not run is never there.
export function can(state: { user: User | null; permissions: string[] | null; tenant?: Tenant | null }, ...rights: string[]): boolean {
  const p = state.permissions;
  const on = rights.filter((r) => { const m = SECTION_MODULE[r.split('.')[0]]; return !m || hasModule(state.tenant, m); });
  if (!on.length) return false;
  if (!p) return hasRole(state.user?.role, ['ADMIN']);
  return p.includes('*') || on.some((r) => p.includes(r));
}

export function useCan(...rights: string[]): boolean {
  return useAuth((s) => can(s, ...rights));
}
